import React, { useState, useEffect, useCallback } from 'react';
import { X, Bookmark, Star } from 'lucide-react';

const ExitIntentPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [browserInfo, setBrowserInfo] = useState({ name: '', isMobile: false, isMac: false });

  // Detect browser and platform
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /iphone|ipad|ipod|android|mobile/i.test(userAgent);
    const isMac = navigator.platform.toLowerCase().includes('mac');
    
    let browserName = 'browser';
    if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      browserName = 'Chrome';
    } else if (userAgent.includes('firefox')) {
      browserName = 'Firefox';
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      browserName = 'Safari';
    } else if (userAgent.includes('edg')) {
      browserName = 'Edge';
    }

    setBrowserInfo({ name: browserName, isMobile, isMac });
  }, []);

  // Check if we should show the prompt (frequency control)
  const shouldShowPrompt = useCallback(() => {
    // Check session flag
    if (sessionStorage.getItem('exitPromptShownSession')) {
      return false;
    }

    // Check 7-day cooldown
    const lastShown = localStorage.getItem('exitPromptLastShown');
    if (lastShown) {
      const daysSinceLastShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      if (daysSinceLastShown < 7) {
        return false;
      }
    }

    return true;
  }, []);

  // Mark prompt as shown
  const markPromptShown = useCallback(() => {
    sessionStorage.setItem('exitPromptShownSession', 'true');
    localStorage.setItem('exitPromptLastShown', Date.now().toString());
  }, []);

  // Desktop: Exit intent detection (mouse leaving viewport at top)
  useEffect(() => {
    if (browserInfo.isMobile) return;

    const handleMouseLeave = (e) => {
      // Only trigger if mouse leaves from top of viewport
      if (e.clientY < 50 && shouldShowPrompt()) {
        setShowPrompt(true);
        markPromptShown();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [browserInfo.isMobile, shouldShowPrompt, markPromptShown]);

  // Mobile: Scroll depth + idle time detection
  useEffect(() => {
    if (!browserInfo.isMobile) return;

    let idleTimer = null;
    let hasScrolledDeep = false;

    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 70) {
        hasScrolledDeep = true;
      }

      // Reset idle timer on scroll
      if (idleTimer) clearTimeout(idleTimer);
      
      // Start idle timer after scroll stops
      idleTimer = setTimeout(() => {
        if (hasScrolledDeep && shouldShowPrompt()) {
          setShowPrompt(true);
          markPromptShown();
        }
      }, 25000); // 25 seconds idle after deep scroll
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [browserInfo.isMobile, shouldShowPrompt, markPromptShown]);

  // Mobile: Back button detection
  useEffect(() => {
    if (!browserInfo.isMobile) return;

    const handlePopState = () => {
      if (shouldShowPrompt()) {
        // Push state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
        setShowPrompt(true);
        markPromptShown();
      }
    };

    // Push initial state
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [browserInfo.isMobile, shouldShowPrompt, markPromptShown]);

  const handleClose = () => {
    setShowPrompt(false);
    setShowInstructions(false);
  };

  const handleBookmarkClick = () => {
    setShowInstructions(true);
  };

  const getBookmarkInstructions = () => {
    const { name, isMobile, isMac } = browserInfo;
    const modifier = isMac ? '⌘' : 'Ctrl';

    if (isMobile) {
      if (name === 'Safari') {
        return (
          <div className="text-center">
            <p className="text-gray-300 mb-2">To bookmark on Safari:</p>
            <ol className="text-left text-gray-400 text-sm space-y-2">
              <li>1. Tap the <strong className="text-cyan-400">Share</strong> button (square with arrow)</li>
              <li>2. Scroll and tap <strong className="text-cyan-400">Add Bookmark</strong></li>
              <li>3. Tap <strong className="text-cyan-400">Save</strong></li>
            </ol>
          </div>
        );
      }
      return (
        <div className="text-center">
          <p className="text-gray-300 mb-2">To bookmark on {name || 'your browser'}:</p>
          <ol className="text-left text-gray-400 text-sm space-y-2">
            <li>1. Tap the <strong className="text-cyan-400">menu</strong> (⋮ or ⋯)</li>
            <li>2. Tap <strong className="text-cyan-400">Add Bookmark</strong> or <strong className="text-cyan-400">⭐</strong></li>
          </ol>
        </div>
      );
    }

    return (
      <div className="text-center">
        <p className="text-gray-300 mb-3">Press this keyboard shortcut:</p>
        <div className="inline-flex items-center gap-2 bg-[#0d0d0d] px-6 py-3 rounded-lg border border-[#2a2a2a]">
          <kbd className="px-3 py-1 bg-[#2a2a2a] text-cyan-400 rounded font-mono text-lg">{modifier}</kbd>
          <span className="text-gray-500">+</span>
          <kbd className="px-3 py-1 bg-[#2a2a2a] text-cyan-400 rounded font-mono text-lg">D</kbd>
        </div>
        <p className="text-gray-500 text-sm mt-3">This will open your browser's bookmark dialog</p>
      </div>
    );
  };

  if (!showPrompt) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Modal */}
        <div 
          className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl max-w-md w-full p-6 shadow-2xl relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {!showInstructions ? (
            /* Initial Prompt */
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Bookmark className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Working on calculations later?
                </h3>
                <p className="text-gray-400">
                  Bookmark PercentageCalculatorPlus to instantly access 40+ free calculators anytime.
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleBookmarkClick}
                  className="w-full py-3 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:from-cyan-400 hover:to-blue-500 transition-all"
                >
                  <Star className="w-5 h-5" />
                  Bookmark This Tool
                </button>
                <button
                  onClick={handleClose}
                  className="w-full py-3 px-4 bg-[#2a2a2a] text-gray-400 rounded-xl font-medium hover:bg-[#333] hover:text-gray-300 transition-all"
                >
                  No thanks
                </button>
              </div>
            </>
          ) : (
            /* Bookmark Instructions */
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">
                  How to Bookmark
                </h3>
                {getBookmarkInstructions()}
              </div>

              <button
                onClick={handleClose}
                className="w-full py-3 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-medium hover:from-cyan-400 hover:to-blue-500 transition-all"
              >
                Got it!
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ExitIntentPrompt;
